import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next, once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, Service } from 'webapp-types';
import {
	Aggregation,
	AnnotationType,
	BillingFeature,
	BlockVisualization,
	CompareType,
	DataSelectType,
	GranulationTypeId,
	MetricDetailsSettingsTab,
} from 'webapp/enums/enums';
import { AppEvent } from 'webapp/enums/events';
import { TrackingEvent } from 'webapp/enums/tracking';
import { getMaxTrainingPeriod } from 'webapp/utils/anomaly-detection';
import { copyObject } from 'webapp/utils/common';
import { formatNumber, useAggregationOnValues } from 'webapp/utils/number';

import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import BlockSettingsPreviewTopbarComponent from 'webapp/components/block/settings/preview-topbar';
import BlockSingleComponent from 'webapp/components/block/single';
import MetricDetailsChartEmptyComponent from 'webapp/components/metric-details/chart-empty';
import MetricDetailsChartLoadingComponent from 'webapp/components/metric-details/chart-loading';
import MetricDetailsExploreDataAnomalyDetectionAnomalyDetectedIconComponent from 'webapp/components/metric-details/explore/data/anomaly-detection/anomaly-detected-icon';
import type { AnomalyConfiguration } from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';
import { AdaptabilityValue } from 'webapp/components/metric-details/explore/data/anomaly-detection/popup-configuration';
import MetricDetailsSettingsTabsComponent from 'webapp/components/metric-details/settings-tabs';
import MetricDetailsSettingsAboutComponent from 'webapp/components/metric-details/settings/about';
import MetricDetailsSettingsAnnotateComponent from 'webapp/components/metric-details/settings/annotate';
import MetricDetailsSettingsVisualizeComponent from 'webapp/components/metric-details/settings/visualize';
import MetricDetailsTableComponent from 'webapp/components/metric-details/table';
import MetricDetailsTopDatapointsDatapointCardComponent from 'webapp/components/metric-details/top-datapoints/datapoint-card';
import MetricDetailsTopDatapointsExploreForecastComponent from 'webapp/components/metric-details/top-datapoints/explore-forecast';
import MetricDetailsTopDatapointsExploreGoalCompareComponent from 'webapp/components/metric-details/top-datapoints/explore-goal-compare';
import MetricDetailsTopDatapointsExplorePreviousPeriodComponent from 'webapp/components/metric-details/top-datapoints/explore-previous-period';
import MetricDetailsTopDatapointsComponent from 'webapp/components/metric-details/top-datapoints/index';
import MetricDetailsTopDatapointsPerformanceSummaryComponent from 'webapp/components/metric-details/top-datapoints/performance-summary';

import type { BlockState, MetricSetup } from 'webapp/models-custom';
import type Block from 'webapp/models-custom/block';
import type BlockSetup from 'webapp/models-custom/block-setup';
import BoardState from 'webapp/models-custom/board-state';
import type DataSelect from 'webapp/models-custom/data-select';
import DateRange from 'webapp/models-custom/date-range';
import DsCompare from 'webapp/models-custom/ds-compare';
import type VisualizationBarChart from 'webapp/models-custom/visualization/bar-chart';
import type VisualizationBase from 'webapp/models-custom/visualization/base';
import type VisualizationBubble from 'webapp/models-custom/visualization/bubble';
import type VisualizationHeatmap from 'webapp/models-custom/visualization/heatmap';
import type VisualizationHorizontalBarChart from 'webapp/models-custom/visualization/horizontal-bar-chart';
import type VisualizationLineChart from 'webapp/models-custom/visualization/line-chart';
import type VisualizationNumber from 'webapp/models-custom/visualization/number';
import type VisualizationPie from 'webapp/models-custom/visualization/pie';
import type VisualizationSpider from 'webapp/models-custom/visualization/spider';
import type VisualizationTable from 'webapp/models-custom/visualization/table';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';

import type { ExploreTabState } from 'webapp/services/metric-details';
import type { RSjqXHR } from 'webapp/services/request';

type InsightsVisualizationWithSeriesOutput =
	| VisualizationLineChart
	| VisualizationBarChart
	| VisualizationHorizontalBarChart
	| VisualizationSpider;
export type InsightsVisualization =
	| InsightsVisualizationWithSeriesOutput
	| VisualizationNumber
	| VisualizationPie
	| VisualizationTable
	| VisualizationHeatmap
	| VisualizationBubble;

interface Args {
	metricSetup: MetricSetup;
	dateRange: DateRange | null;
	chartState: MetricInsightsChartState;
	forceLoadingState: boolean;
	origin: string;
	initialTabState: ExploreTabState;
	onTabStateChange: (state: ExploreTabState) => void;
	onAnomalyStateChanged: (changes: Partial<ExploreTabState>) => void;
}

interface MetricDetailsExploreDataComponentInterface {
	Args: Args;
}

export class MetricInsightsChartState {
	@tracked isVisualizationModuleDataLoading = false;
	@tracked isDataEmpty = true;
	@tracked isHorizonPollingExhausted = false;
	@tracked block: Block | null = null;
	@tracked blockState: BlockState | null = null;

	// The block this chart is currently showing. A new one is built whenever the metric or
	// timeframe changes, so a data request can compare against this to tell whether the
	// block it was started for is still the one on screen. Deliberately untracked — it is
	// bookkeeping for in-flight requests, never rendered.
	activeBlockUUID = '';

	get visualization() {
		const visualization = this.block?.blockSetup?.visualization;

		return visualization ? (visualization as InsightsVisualization) : undefined;
	}
}

export const anomalyConfigurationInitialValues = {
	anomalyAdaptability: AdaptabilityValue.MEDIUM,
	anomalySensitivity: 0.2,
	anomalyTrainingPeriod: 0,
};

export default class MetricDetailsExploreDataComponent extends Component<MetricDetailsExploreDataComponentInterface> {
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('store') declare storeService: Service.Store;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('garbage-collector') declare garbageCollectorService: Service.GarbageCollector;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('billing') declare billingService: Service.Billing;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;

	@tracked boardState: BoardState | null = null;
	@tracked _isAnomalySwitchChecked = false;
	@tracked anomalyConfiguration: AnomalyConfiguration = anomalyConfigurationInitialValues;

	private isSyncingFromBlock = false;

	loadingMessages: string[] = [
		'Fetching historical data',
		'Crunching the numbers',
		'Formatting the data for display',
		'Almost there, just a few more seconds',
		'Waiting for data from the source',
		'Still loading, please wait',
	];
	loadingMessageIntervalTime = (15 * 1000) / 4;

	private visualizationModuleDataXHR: RSjqXHR | null = null;
	private blockUUID = this.generateUUID() as string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			if (this.args.initialTabState.anomalyEnabled !== null) {
				this.changeAnomalySwitchState(this.args.initialTabState.anomalyEnabled, true);
			} else {
				this.changeAnomalySwitchState(false, true);
			}

			if (this.args.initialTabState.anomalyConfig !== null) {
				this.changeAnomalyConfiguration(this.args.initialTabState.anomalyConfig, true);
			} else {
				this.changeAnomalyConfiguration(anomalyConfigurationInitialValues, true);
			}

			this.buildInitialBlock();

			this.eventManagerService.on(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this, () => {
				this.applyTabStateToBlock();
			});
		});
	}

	get block(): Block | null {
		return this.boardState?.selectedBlock ?? null;
	}

	get visualization() {
		const visualization = this.args.initialTabState.visualization;

		if (
			visualization === null ||
			!this.mmBlocksService.switchableVisualizationListForMetricDetails
				.map((v) => v.id)
				.includes(visualization)
		) {
			return BlockVisualization.LINE_CHART;
		}

		return visualization;
	}

	get isVisualizationBarOrLine() {
		return (
			this.visualization === BlockVisualization.LINE_CHART ||
			this.visualization === BlockVisualization.BAR_CHART
		);
	}

	get isAnomalyDetectionEnabled() {
		return this.billingService.checkIfCan(BillingFeature.ANOMALY_DETECTION);
	}

	get isGranulationValid() {
		return (
			this.granulationTypeId === GranulationTypeId.DAILY ||
			this.granulationTypeId === GranulationTypeId.WEEKLY ||
			this.granulationTypeId === GranulationTypeId.MONTHLY
		);
	}

	get isAnomalySwitchChecked() {
		return this.isSingleOutput && this.isGranulationValid
			? this._isAnomalySwitchChecked
			: false;
	}

	get metricSetup() {
		return this.args.metricSetup;
	}

	get isSingleOutput() {
		return this.metricSetup.isSingleOutput;
	}

	get granulationTypeId() {
		return this.args.dateRange?.granulationTypeId;
	}

	get themeId() {
		return this.navigationService.theme.id;
	}

	get isEmpty() {
		const block = this.block;

		return !!block && block.isDataEmpty && !block.isSyncing;
	}

	get isLoading() {
		return (
			this.args.chartState.isVisualizationModuleDataLoading || !!this.args.forceLoadingState
		);
	}

	get hideDataSections() {
		return this.isEmpty && !this.isLoading;
	}

	get activeCompare(): DsCompare | null {
		return this.block?.blockSetup?.firstShownDataSelect?.limitations.compare ?? null;
	}

	get pendingCompare(): DSSetup.Compare | null | undefined {
		return this.args.initialTabState.compare;
	}

	get isActiveCompareGoal(): boolean {
		const compare = this.activeCompare;
		return !!compare?.isGoal && !!compare.metricSetup.key;
	}

	get isActiveCompareNone(): boolean {
		// while the chart is loading, the block's compare is unsettled and briefly reads as None
		return !this.isLoading && !!this.activeCompare?.isNone;
	}

	get isPendingCompareGoal(): boolean {
		return this.pendingCompare?.type === CompareType.GOAL;
	}

	// args.dateRange lags one render — read the block's copy for same-render reactions
	get effectiveDateRange(): DateRange | null {
		return this.block?.blockSetup?.limitations?.dateRange ?? this.args.dateRange;
	}

	get dateRangeLabel(): string {
		const dateRange = this.effectiveDateRange;
		if (!dateRange) return '';
		return dateRange.rangeTitleUX || dateRange.rangeTypeId || '';
	}

	get dateRangeText(): string | undefined {
		const point = this.primaryNumberPoint;
		return point?.timeLabelLong ?? point?.timeLabel;
	}

	get primaryNumberPoint(): DChartPrimaryPoint | null {
		return this.summaryPoints[0] ?? null;
	}

	get outputNumberPoints(): {
		point: DChartPrimaryPoint;
		title: string;
	}[] {
		return this.summaryPoints.map((point, index) => ({
			point,
			title: point.title || `Output ${index + 1}`,
		}));
	}

	// visualizations like line-chart/gauge curate have numberPoints
	// some visualization like bar-chart dont have numberPoints, use allMainPoints to get the summary points
	get summaryPoints(): DChartPrimaryPoint[] {
		const visualization = this.args.chartState.visualization as VisualizationBase | null;
		if (!visualization) return [];
		return visualization.numberPoints.length
			? visualization.numberPoints
			: visualization.getAllMainPoints();
	}

	get settingsTabs(): MetricDetailsSettingsTab[] {
		const tabs: MetricDetailsSettingsTab[] = [
			MetricDetailsSettingsTab.VISUALIZE,
			MetricDetailsSettingsTab.ANNOTATE,
		];
		if (
			this.metricSetup.metric?.description ||
			(this.metricSetup.metric?.key && this.metricSetup.metric?.spaceAccessId)
		) {
			tabs.push(MetricDetailsSettingsTab.ABOUT);
		}
		return tabs;
	}

	get annotationCount(): number {
		const annotations = this.block?.sample?.sampledata?.annotations || [];
		return annotations.filter((a) => a.type === AnnotationType.DEFAULT).length;
	}

	get badgeCounts(): Partial<Record<MetricDetailsSettingsTab, number>> {
		return {
			[MetricDetailsSettingsTab.ANNOTATE]: this.annotationCount,
		};
	}

	// --- TABLE ---
	get tableDataSelects(): DataSelect[] {
		const block = this.block;
		if (!block) return [];

		const primaryDs = block.blockSetup.firstShownDataSelect;
		if (!primaryDs) return [];

		const dataSelects: DataSelect[] = [primaryDs];

		// include goal dataSelects if present
		const secondaries = block.blockSetup.getSecondaryDataSelectsForDataSelect(primaryDs) || [];
		const goalDataSelects = secondaries.filter((ds) => ds.isGoal);
		dataSelects.push(...goalDataSelects);

		return dataSelects;
	}

	get tableHeaderLabel(): string {
		return this.args.metricSetup.metric?.name || '';
	}

	get tableRows(): {
		dataSelect: DataSelect;
		outputIndex: number;
		label: string;
		colorClass: string;
		cellClass: string;
		summaryValue: string;
		summaryCompareValue: string | null;
	}[] {
		const dataSelects = this.tableDataSelects;
		if (!dataSelects.length) return [];

		const rows: {
			dataSelect: DataSelect;
			outputIndex: number;
			label: string;
			colorClass: string;
			cellClass: string;
			summaryValue: string;
			summaryCompareValue: string | null;
		}[] = [];

		const primaryDs = dataSelects[0]!;

		const isSingleOutput = primaryDs.outputs.length === 1;

		const colorMap = new Map<string, string | number>();
		for (const { point } of this.outputNumberPoints) {
			const msd = point.metricSelectionData;
			if (msd && !point.hasNoColorIndexOrClass) {
				colorMap.set(
					`${Object.entries(msd.dimensionValues).join(':')}:${msd.unit}`,
					point.colorIndexOrClass,
				);
			}
		}

		primaryDs.outputs.forEach((output, index) => {
			const summaryData = this.getSummaryForOutput(primaryDs, index);

			const chartColor = colorMap.get(
				`${Object.entries(output.dimensionValues).join(':')}:${output.unit}`,
			);
			const colorClass =
				chartColor !== undefined
					? `highcharts-color-${chartColor}`
					: `highcharts-color-${index}`;

			rows.push({
				dataSelect: primaryDs,
				outputIndex: index,
				label: output.shortTitleWithoutUnit,
				colorClass,
				cellClass: isSingleOutput ? 'highlighted-row' : '',
				summaryValue: summaryData.value,
				summaryCompareValue: summaryData.compareValue,
			});
		});

		// add goal rows if goal dataSelects exist on the block
		const block = this.block!;
		const secondaries = block.blockSetup.getSecondaryDataSelectsForDataSelect(primaryDs) || [];
		const goalDataSelects = secondaries.filter((ds) => ds.isGoal);
		goalDataSelects.forEach((goalDs) => {
			if (goalDs?.outputs?.[0]) {
				const goalSummary = this.getSummaryForOutput(goalDs, 0);
				const goal = this.mmGoalsService.getGoalForGoalKeyLong(goalDs.metricSetupKey);
				rows.push({
					dataSelect: goalDs,
					outputIndex: 0,
					label: goal?.title || 'Goal',
					colorClass: 'highcharts-color-goal',
					cellClass: 'goal-row',
					summaryValue: goalSummary.value,
					summaryCompareValue: goalSummary.compareValue,
				});
			}
		});

		return rows;
	}

	private getSummaryForOutput(
		dataSelect: DataSelect,
		outputIndex: number,
	): { value: string; compareValue: string | null } {
		const dataRoot = dataSelect.dataRoot;
		const output = dataRoot?.outputs[outputIndex];
		if (!output) {
			return { value: '-', compareValue: null };
		}

		const values = output.points.filter((p) => p.hasValue).map((p) => p.value);
		const avg = useAggregationOnValues(Aggregation.AVG, values, [], 0);

		if (avg === null) {
			return { value: '-', compareValue: null };
		}

		const format = output.format || '';
		const scale = dataRoot?.scale ?? null;

		// when the user's compare is a Goal, the goal gets its own dedicated row
		// dont show compare value in first row
		let compareValue: string | null = null;
		if (!this.isActiveCompareGoal) {
			const userCompareId = dataSelect.limitations.compare?.id;
			const userCompareOutput = output.compareOutputs.find(
				(co) => co.compareSettings?.id === userCompareId,
			);
			if (userCompareOutput) {
				const compareValues = userCompareOutput.points
					.filter((cp) => cp.hasValue)
					.map((cp) => cp.value);
				const compareAvg = useAggregationOnValues(Aggregation.AVG, compareValues, [], 0);
				if (compareAvg !== null) {
					compareValue = formatNumber(compareAvg, format, { scale });
				}
			}
		}

		return { value: formatNumber(avg, format, { scale }), compareValue };
	}

	private generateUUID(string = true) {
		const numericId = this.metricDetailsService.generateMetricInsightsUUID();
		return string ? numericId.toString() : numericId;
	}

	private buildInitialBlock() {
		const dateRange: DateRangeInterface.FormulaInterface | null = this.args.dateRange
			? copyObject(this.args.dateRange?.formula)
			: null;

		this.blockUUID = this.generateUUID() as string;

		const params = {
			id: this.blockUUID,
			order: 0,
			position: {
				x: 0,
				y: 0,
				w: 16,
				h: 8,
				m: true,
			},
			visualization: this.visualization,
			isSpline: false,
			name: '',
		};

		const block = this.mmBlocksService.createNewModel(params);
		this.boardState = BoardState.createForSingleBlock(block);
		next(() => {
			this.args.chartState.block = block;
			this.args.chartState.blockState = this.boardState!.getBlockState(block) ?? null;
		});

		const blockSetup = block.blockSetup;
		blockSetup.isAutosaveOn = false;

		const dateRangeCopy: DateRangeInterface.FormulaInterface | null = copyObject(dateRange),
			granulation = this.block?.blockSetup.limitations.dateRange?.formula.granulation,
			newGranulation = granulation?.isWholeRange ? granulation : dateRangeCopy?.granulation;

		let dateRangeWithGranulation: DateRangeInterface.FormulaInterface | undefined;
		if (dateRangeCopy?.range && newGranulation) {
			dateRangeWithGranulation = {
				range: dateRangeCopy.range,
				granulation: newGranulation,
			};
		}

		const firstDsId = this.generateUUID(),
			metricSetupData = this.metricSetup.metricSetupData!;

		const primaryDsProps: Parameters<BlockSetup['createPrimaryMainDataSelect']>[0] = {
			id: firstDsId,
			blockId: block.id,
			dateRange: dateRangeWithGranulation,
			metricSetupKey: metricSetupData.key,
			metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
			metricSetupDimensions: metricSetupData.dimensions,
			metricSetupFilters: metricSetupData.filters,
		};

		const hasExplicitCompareSelection = this.pendingCompare !== undefined;

		if (this.pendingCompare) {
			primaryDsProps.compares = [this.pendingCompare];
		}

		/* firstDs is what blockSetup.firstShownDataSelect would return in next runloop */
		const firstDs = blockSetup.createPrimaryMainDataSelect(
			primaryDsProps,
			hasExplicitCompareSelection,
		);

		if (this.applyMultiAttributeFallbackIfNeeded(blockSetup)) {
			return;
		}

		const firstDsCompares = firstDs.limitations.compareCMs;
		firstDsCompares.forEach((compare) => {
			compare.id = this.generateUUID(false) as number;
		});
		firstDs.updateComparesFormulaArrayFromCompareModels(firstDsCompares);

		const compareDs = this.addCompareSecondary(blockSetup, firstDs);
		const numberDs = this.addNumberSecondary(blockSetup, firstDs);
		const goalDataSelects = this.addGoalSecondaries(blockSetup, firstDs);
		const anomalyDs = this.addAnomalySecondary(blockSetup, firstDs);

		const newlyAdded: DataSelect[] = [firstDs];
		if (compareDs) newlyAdded.push(compareDs);
		if (numberDs) newlyAdded.push(numberDs);
		newlyAdded.push(...goalDataSelects);
		if (anomalyDs) newlyAdded.push(anomalyDs);

		this.args.chartState.isDataEmpty = true;
		this.args.chartState.isVisualizationModuleDataLoading = true;
		next(() => {
			this.fakePersistDataSelects(newlyAdded);

			this.visualizationModuleDataXHR =
				this.metricDetailsService.requestVisualizationModuleData(
					this.block!,
					this.args.chartState,
					this.blockUUID,
					this.visualizationModuleDataXHR,
				);
		});
	}

	private applyMultiAttributeFallbackIfNeeded(blockSetup: BlockSetup): boolean {
		if (
			blockSetup.visualization.allowMultipleMetricsOrDimensions ||
			!this.metricSetup.isOutputMultipleAttributes
		) {
			return false;
		}
		this.infolayerService.warning(
			'The selected visualization supports only one attribute but multiple were found. Extra attributes removed.',
		);
		next(() => {
			this.metricSetup.setFiltersFromAttributeNames([
				this.metricSetup.firstAttributeNameFromBasicFilters,
			]);
		});
		this.args.chartState.isDataEmpty = true;
		return true;
	}

	private addCompareSecondary(blockSetup: BlockSetup, primaryDs: DataSelect): DataSelect | null {
		if (!blockSetup.visualization.showAdditionalLines || this.isPendingCompareGoal) {
			return null;
		}
		return blockSetup.addSecondaryDataSelectForDataSelect(primaryDs, DataSelectType.COMPARE, {
			id: this.generateUUID(),
			updateFrom: primaryDs.id,
		});
	}

	private addNumberSecondary(blockSetup: BlockSetup, primaryDs: DataSelect): DataSelect | null {
		if (!blockSetup.visualization.canShowAndDisableNumberOnMetric) {
			return null;
		}

		const dateRangeCopy = primaryDs.limitations.dateRange?.createCopy();
		if (!dateRangeCopy) {
			return null;
		}
		dateRangeCopy.updateFromPredefinedGranulationId(GranulationTypeId.TOTAL);

		// copy primary's compares, swap IDs
		const compares = copyObject(primaryDs.compares);
		compares.forEach((c) => {
			c.id = this.generateUUID(false) as number;
		});
		const metricSetupData = this.metricSetup.metricSetupData!;

		return blockSetup.createDataSelect({
			id: this.generateUUID(),
			updateFrom: primaryDs.id,
			type: DataSelectType.MAIN,
			metricSetupKey: metricSetupData.key,
			metricSetupSpaceAccessId: metricSetupData.spaceAccessId,
			metricSetupDimensions: metricSetupData.dimensions ?? [],
			metricSetupFilters: metricSetupData.filters ?? null,
			dateRange: dateRangeCopy.formula,
			compares,
		});
	}

	private addGoalSecondaries(blockSetup: BlockSetup, primaryDs: DataSelect): DataSelect[] {
		if (!blockSetup.visualization.showAdditionalLines || !this.isSingleOutput) {
			return [];
		}
		const validGoals = this.mmGoalsService.getValidGoalsForMetricSetup(this.metricSetup);
		const activeGoalKey = this.isPendingCompareGoal
			? this.pendingCompare?.metricSetup?.key
			: undefined;
		const goalsToRender = activeGoalKey
			? validGoals.filter((g) => g.goalKeyLong === activeGoalKey)
			: validGoals;

		const dateRangeFormula = primaryDs.limitations.dateRange?.getFormula();
		const added: DataSelect[] = [];
		goalsToRender.forEach((goal) => {
			const goalProps: Parameters<BlockSetup['addSecondaryDataSelectForDataSelect']>[2] = {
				id: this.generateUUID(),
				updateFrom: primaryDs.id,
				metricSetupKey: goal.goalKeyLong,
				metricSetupSpaceAccessId: goal.mainMetricSetupSpaceAccessId,
			};
			if (dateRangeFormula) {
				goalProps.dateRange = dateRangeFormula;
			}
			const ds = blockSetup.addSecondaryDataSelectForDataSelect(
				primaryDs,
				DataSelectType.GOAL,
				goalProps,
			);
			if (ds) added.push(ds);
		});
		return added;
	}

	private addAnomalySecondary(blockSetup: BlockSetup, primaryDs: DataSelect): DataSelect | null {
		const granulationTypeId = this.granulationTypeId;
		if (
			!this.isAnomalySwitchChecked ||
			!this.isVisualizationBarOrLine ||
			!this.isAnomalyDetectionEnabled ||
			!granulationTypeId
		) {
			return null;
		}
		const { num } = getMaxTrainingPeriod(granulationTypeId);
		if (this.anomalyConfiguration.anomalyTrainingPeriod > num) {
			this.anomalyConfiguration = {
				...this.anomalyConfiguration,
				anomalyTrainingPeriod: num,
			};
			this.args.onAnomalyStateChanged({ anomalyConfig: this.anomalyConfiguration });
		}
		return blockSetup.addSecondaryDataSelectForDataSelect(primaryDs, DataSelectType.ANOMALY, {
			id: this.generateUUID(),
			...this.anomalyConfiguration,
		});
	}

	private fakePersistDataSelects(dataSelects: DataSelect[]): void {
		if (!dataSelects.length) return;
		const fakeSavedDataSelects = dataSelects.map((ds) => ds.toSerializedJSON(true));
		dataSelects.forEach((ds) => ds.model.deleteRecord());
		this.storeService.pushPayload({ dataSelects: fakeSavedDataSelects });
	}

	@action
	applyTabStateToBlock(): void {
		// a metric switch must always re-resolve data — otherwise the guards/no-ops below
		// can swallow the refetch and leave the previous metric's stale empty sample
		const primaryKey = this.block?.blockSetup.firstShownDataSelect?.metricSetupKey;
		const isMetricSwitch =
			!!this.metricSetup.key && !!primaryKey && primaryKey !== this.metricSetup.key;

		if (this.isSyncingFromBlock) {
			this.isSyncingFromBlock = false;
			// the one-shot sync guard must not abort a real metric switch
			if (!isMetricSwitch) {
				return;
			}
		}
		if (!this.block || this.args.forceLoadingState || !this.metricSetup.key) {
			return;
		}

		const blockSetup = this.block.blockSetup;
		if (this.applyMultiAttributeFallbackIfNeeded(blockSetup)) {
			return;
		}

		// mutate the primary dataSelect's metricSetup in place (filter / dimension / key changes)
		const primaryDs = blockSetup.firstShownDataSelect;
		if (primaryDs) {
			primaryDs.updateMetricSetupData(this.metricSetup.metricSetupData);
		}

		// visualization
		if (blockSetup.visualization.visualizationType !== this.visualization) {
			void blockSetup.changeVisualization(this.visualization);
		}

		// date range + granulation (preserve whole-range granulation, same logic as buildInitialBlock)
		if (this.args.dateRange && primaryDs) {
			const sourceFormula = this.args.dateRange.formula;
			const currentGranulation = primaryDs.limitations.dateRange?.formula.granulation;
			const newGranulation = currentGranulation?.isWholeRange
				? currentGranulation
				: sourceFormula.granulation;
			const dateRangeWithGranulation: DateRangeInterface.FormulaInterface = {
				range: sourceFormula.range,
				...(newGranulation ? { granulation: newGranulation } : {}),
			};
			const nextDateRange = DateRange.createFromFormula(dateRangeWithGranulation);

			const blockDateRangeBefore = blockSetup.limitations.dateRange;
			const dateRangeChanged = !blockDateRangeBefore?.isEqual(nextDateRange.formula);
			blockSetup.changeDateRange(nextDateRange);

			// changeDateRange resets granulation to the optimal default, dropping the cumulative setting
			// re-apply it from the tab state
			const cumulative = !!newGranulation?.cumulative;
			const blockDateRange = blockSetup.limitations.dateRange;
			const cumulativeChanged =
				!!blockDateRange && blockDateRange.granulationCumulative !== cumulative;
			if (cumulativeChanged) {
				blockSetup.changeCumulative(cumulative);
			}

			// new metric shares the previous range/granulation so both calls above no-op;
			// force the re-push the cumulative-toggle path always does, else data stays stale
			if (isMetricSwitch && !dateRangeChanged && !cumulativeChanged && blockDateRange) {
				void blockSetup.updateDataSelectsFromPrimaryDateRange(blockDateRange);
			}
		}

		// compare on the primary dataSelect
		if (primaryDs && this.pendingCompare !== undefined) {
			const currentCompare = primaryDs.limitations.compare;
			if (currentCompare && !currentCompare.isEqual(this.pendingCompare)) {
				const nextCompare = DsCompare.createFromFormula(this.pendingCompare);
				blockSetup.changeCompare(primaryDs, currentCompare, nextCompare);
			}
		}

		this.args.chartState.isVisualizationModuleDataLoading = true;

		// wait for queued blockSetup dataSelect updates before rebuilding + refetching
		const block = this.block;
		void block.blockSetup.afterSaves().then(() => {
			this.rebuildSecondaryDataSelects();
			next(() => {
				this.args.chartState.isVisualizationModuleDataLoading = true;
				// Discard + refetch must be synchronous — an async gap between them causes intermittent "No data"
				block.discardSampleData();
				this.visualizationModuleDataXHR =
					this.metricDetailsService.requestVisualizationModuleData(
						block,
						this.args.chartState,
						this.blockUUID,
						this.visualizationModuleDataXHR,
					);
			});
		});
	}

	private syncTabStateFromBlock(): void {
		const blockSetup = this.block?.blockSetup;
		if (!blockSetup) return;
		const primaryDs = blockSetup.firstShownDataSelect;
		const blockCompareFormula = primaryDs?.limitations.compare?.getFormula() ?? null;
		// keep compare undefined (untouched) instead of null (explicitly None) until user picks one
		const compareForState =
			blockCompareFormula === null && this.args.initialTabState.compare === undefined
				? undefined
				: blockCompareFormula;
		this.isSyncingFromBlock = true;
		this.args.onTabStateChange({
			...this.args.initialTabState,
			dateRange: blockSetup.limitations.dateRange?.formula ?? null,
			visualization: blockSetup.visualization.visualizationType,
			compare: compareForState,
		});
	}

	@action
	onBlockChange(): void {
		const block = this.block;
		if (!block) return;
		this.args.chartState.isVisualizationModuleDataLoading = true;
		// wait for queued blockSetup dataSelect updates before rebuilding + refetching
		// sync tab state only after save — the topbar updates the date range async
		// synching early pushes the previous range up, leaving @dateRange one step behind the block
		void block.blockSetup.afterSaves().then(() => {
			this.syncTabStateFromBlock();
			this.rebuildSecondaryDataSelects();
			// Discard + refetch must be synchronous — an async gap between them causes intermittent "No data"
			block.discardSampleData();
			this.refetchVisualizationData();
		});
	}

	@action
	changeCumulative(value: boolean): void {
		const blockSetup = this.block?.blockSetup;
		if (!blockSetup) return;
		blockSetup.changeCumulative(value);
		// tab-state persistence flows through onBlockChange → syncTabStateFromBlock
		this.onBlockChange();

		const metric = this.metricSetup.metric;
		this.analyticsService.event(
			value
				? TrackingEvent.METRIC_DETAILS_CUMULATIVE_ON
				: TrackingEvent.METRIC_DETAILS_CUMULATIVE_OFF,
			{
				origin: 'metrics_detail',
				datasource: metric?.dataSourceTypeKey,
				metrics: metric?.name,
			},
		);
	}

	private rebuildSecondaryDataSelects(): void {
		const block = this.block;
		if (!block) return;
		const blockSetup = block.blockSetup;
		const primaryDs = blockSetup.firstShownDataSelect;
		if (!primaryDs) return;

		// unload existing secondaries; we'll rebuild fresh from current state
		blockSetup
			.getSecondaryDataSelectsForDataSelect(primaryDs)
			.forEach((ds) => ds.model.unloadRecord());

		// sync compareCMs onto primary before adding COMPARE (it reads compareCMs[0] for offset)
		const firstDsCompares = primaryDs.limitations.compareCMs;
		firstDsCompares.forEach((compare) => {
			if (!compare.id) {
				compare.id = this.generateUUID(false) as number;
			}
		});
		primaryDs.updateComparesFormulaArrayFromCompareModels(firstDsCompares);

		const newlyAdded: DataSelect[] = [];
		const compareDs = this.addCompareSecondary(blockSetup, primaryDs);
		if (compareDs) newlyAdded.push(compareDs);
		const numberDs = this.addNumberSecondary(blockSetup, primaryDs);
		if (numberDs) newlyAdded.push(numberDs);
		newlyAdded.push(...this.addGoalSecondaries(blockSetup, primaryDs));
		const anomalyDs = this.addAnomalySecondary(blockSetup, primaryDs);
		if (anomalyDs) newlyAdded.push(anomalyDs);

		this.fakePersistDataSelects(newlyAdded);
	}

	@action
	onDidUpdateArgs(_element: HTMLElement): void {
		once(this, this.applyTabStateToBlock);
	}

	@action
	refetchVisualizationData() {
		if (this.block) {
			const chartState = this.args.chartState;
			if (chartState) {
				chartState.isVisualizationModuleDataLoading = true;
			}

			this.visualizationModuleDataXHR =
				this.metricDetailsService.requestVisualizationModuleData(
					this.block,
					this.args.chartState,
					this.blockUUID,
					this.visualizationModuleDataXHR,
				);
		}
	}

	@action
	teardownView(): void {
		// Use dom event, not willDestroy cause user might quickly navigate through visited routes
		this.eventManagerService.off(AppEvent.METRIC_INSIGHTS_METRIC_EDITED, this);
	}

	@action
	changeAnomalySwitchState(newValue: boolean, skipTracking = false): void {
		this._isAnomalySwitchChecked = newValue;
		this.applyTabStateToBlock();
		this.args.onAnomalyStateChanged({ anomalyEnabled: newValue });

		if (newValue && !skipTracking) {
			const metricSetup = this.metricSetup,
				metricName = metricSetup.metric?.name;

			this.analyticsService.event(TrackingEvent.ANOMALIES_HIGHLIGHTED, {
				datasource: metricSetup.sourceType?.key,
				metrics: metricName ? [metricName] : [],
				granulation: this.args.dateRange?.granulationTypeId,
				date_range: this.args.dateRange?.rangeTypeId,
				visualization:
					this.visualization === BlockVisualization.LINE_CHART ? 'line' : 'bar',
			});
		}
	}

	@action
	changeAnomalyConfiguration(configuration: AnomalyConfiguration, skipTracking = false): void {
		this.anomalyConfiguration = configuration;
		this.applyTabStateToBlock();
		this.args.onAnomalyStateChanged({ anomalyConfig: configuration });

		if (!skipTracking) {
			this.analyticsService.event(TrackingEvent.ANOMALY_DETECTION_PARAMETERS_CHANGED, {
				sensitivity: configuration.anomalySensitivity,
				training_period: configuration.anomalyTrainingPeriod,
				adaptability: configuration.anomalyAdaptability,
			});
		}
	}

	@action
	retryAnomalyRequest(): void {
		this.applyTabStateToBlock();
	}

	override willDestroy() {
		super.willDestroy();

		this.visualizationModuleDataXHR?.abort();

		this.garbageCollectorService._unloadBlockData(this.blockUUID);

		const block = this.block;
		if (block) {
			block.model.unloadRecord();
		}
	}
	<template>
		<div
			class="metric-explore-data module-box
				{{unless @metricSetup.metric 'module-box--empty'}}"
			{{didUpdate
				this.onDidUpdateArgs
				@metricSetup.key
				@metricSetup.spaceAccessId
				@metricSetup.dimensions
				@metricSetup.filtersData
				@dateRange.formulaId
				@forceLoadingState
				@initialTabState.visualization
				@initialTabState.compare
			}}
			{{willDestroy this.teardownView}}
		>
			{{#if @metricSetup.metric}}
				{{#if @chartState.blockState}}
					<div class="metric-details__topbar">
						<BlockSettingsPreviewTopbarComponent
							@blockState={{@chartState.blockState}}
							@theme={{this.themeId}}
							@showCumulative={{false}}
							@showMasterFilter={{false}}
							@showCompare={{true}}
							@showVisualizationsAsGrid={{true}}
							@origin="metrics_detail"
							@setModelIsDirty={{this.onBlockChange}}
						/>
					</div>
				{{/if}}

				<MetricDetailsExploreDataAnomalyDetectionAnomalyDetectedIconComponent />

				<div class="metric-details__data-grid">
					<div class="metric-explore-data-board metric-details__chart-column">
						{{#unless this.hideDataSections}}
							<MetricDetailsTopDatapointsComponent
								class="colorTheme-1
									{{unless
										this.metricSetup.isSingleOutput
										'top-datapoints--multi-output'
									}}"
							>
								{{#if this.metricSetup.isSingleOutput}}
									<MetricDetailsTopDatapointsDatapointCardComponent
										@label={{this.dateRangeLabel}}
										@dateText={{this.dateRangeText}}
										@valuePoint={{this.primaryNumberPoint}}
										@size="primary"
										@isLoading={{this.isLoading}}
									/>
									{{#unless this.isActiveCompareNone}}
										{{#if this.isActiveCompareGoal}}
											<MetricDetailsTopDatapointsExploreGoalCompareComponent
												@chartState={{@chartState}}
												@metricSetup={{@metricSetup}}
												@dateRange={{@dateRange}}
												@forceLoadingState={{@forceLoadingState}}
											/>
										{{else}}
											<MetricDetailsTopDatapointsExplorePreviousPeriodComponent
												@chartState={{@chartState}}
												@metricSetup={{@metricSetup}}
												@dateRange={{@dateRange}}
												@forceLoadingState={{@forceLoadingState}}
											/>
										{{/if}}
									{{/unless}}
									<MetricDetailsTopDatapointsExploreForecastComponent
										@metricSetup={{@metricSetup}}
										@dateRange={{this.effectiveDateRange}}
									/>
									<MetricDetailsTopDatapointsPerformanceSummaryComponent
										@chartState={{@chartState}}
										@metricSetup={{@metricSetup}}
										@dateRange={{@dateRange}}
										@forceLoadingState={{@forceLoadingState}}
									/>
								{{else}}
									<MetricDetailsTopDatapointsPerformanceSummaryComponent
										@chartState={{@chartState}}
										@metricSetup={{@metricSetup}}
										@dateRange={{@dateRange}}
										@forceLoadingState={{@forceLoadingState}}
									/>
									{{#each this.outputNumberPoints as |output|}}
										<MetricDetailsTopDatapointsDatapointCardComponent
											@label={{output.title}}
											@labelColorClass={{output.point.colorIndexOrClass}}
											@valuePoint={{output.point}}
											@comparePoints={{output.point.comparePoints}}
											@skipCompareText={{true}}
											@isLoading={{this.isLoading}}
										/>
									{{/each}}
								{{/if}}
							</MetricDetailsTopDatapointsComponent>
						{{/unless}}
						{{#if this.isLoading}}
							<MetricDetailsChartLoadingComponent
								@messages={{this.loadingMessages}}
								@intervalTime={{this.loadingMessageIntervalTime}}
							/>
						{{else if this.isEmpty}}
							<MetricDetailsChartEmptyComponent
								@title={{if
									@chartState.isHorizonPollingExhausted
									"Data is still being prepared"
								}}
								@subtitle={{if
									@chartState.isHorizonPollingExhausted
									"This process can take a few minutes. Please try again shortly."
								}}
							/>
						{{else if this.boardState}}
							<BlockSingleComponent
								@boardState={{this.boardState}}
								@skipBlockFetchLoader={{true}}
								@skipActions={{this.metricDetailsService.isMobileMode}}
							/>
						{{/if}}
					</div>

					{{! Not in the mobile drawer: Annotate and the verification toggle in About write. }}
					{{#unless this.metricDetailsService.isMobileMode}}
						<MetricDetailsSettingsTabsComponent
							@tabs={{this.settingsTabs}}
							@badgeCounts={{this.badgeCounts}}
							as |tabState|
						>
							{{#if tabState.isVisualize}}
								<MetricDetailsSettingsVisualizeComponent
									@metricSetup={{@metricSetup}}
									@blockM={{this.block}}
									@dateRange={{@dateRange}}
									@origin="metrics_detail"
									@anomalyConfiguration={{this.anomalyConfiguration}}
									@isAnomalySwitchChecked={{this.isAnomalySwitchChecked}}
									@onChangeCumulative={{this.changeCumulative}}
									@onChangeAnomalySwitchState={{this.changeAnomalySwitchState}}
									@onChangeAnomalyConfiguration={{this.changeAnomalyConfiguration}}
								/>
							{{else if tabState.isAnnotate}}
								<MetricDetailsSettingsAnnotateComponent
									@blockM={{this.block}}
									@metricSetup={{@metricSetup}}
									@isLoading={{this.isLoading}}
								/>
							{{else if tabState.isAbout}}
								{{#if @metricSetup.metric}}
									<MetricDetailsSettingsAboutComponent
										@metric={{@metricSetup.metric}}
									/>
								{{/if}}
							{{/if}}
						</MetricDetailsSettingsTabsComponent>
					{{/unless}}
				</div>

				{{#unless this.hideDataSections}}
					<div class="colorTheme-1">
						<MetricDetailsTableComponent
							@dataSelects={{this.tableDataSelects}}
							@labelColumnTitle={{this.tableHeaderLabel}}
							@summaryColumnTitle="Average"
							@summaryColumnOnLeft={{true}}
						>
							<:body as |table|>
								{{#if this.isLoading}}
									<table.Row @dataSelect={{null}} @isLoading={{true}}>
										<:header>
											<span
												class="metric-detail-color-dot highcharts-color-0"
											></span>
											<span>{{@metricSetup.metric.name}}</span>
										</:header>
										<:summary>
											<span class="skeleton skeleton-text-medium"></span>
										</:summary>
									</table.Row>
								{{else}}
									{{#each this.tableRows as |row|}}
										<table.Row
											@dataSelect={{row.dataSelect}}
											@outputIndex={{row.outputIndex}}
											@cellClass={{row.cellClass}}
										>
											<:header>
												<span
													class="metric-detail-color-dot
														{{row.colorClass}}"
												></span>
												<span>{{row.label}}</span>
											</:header>
											<:summary>
												<div class="summary-cell-values">
													<span
														class="data-value primary"
													>{{row.summaryValue}}
													</span>
													{{#if row.summaryCompareValue}}
														<span class="data-value compare">
															{{row.summaryCompareValue}}
														</span>
													{{/if}}
												</div>
											</:summary>
										</table.Row>
									{{/each}}
								{{/if}}
							</:body>
						</MetricDetailsTableComponent>
					</div>
				{{/unless}}
			{{/if}}
		</div>
	</template>
}
